import { useState, useMemo, memo } from "react";
import type { ToolUIPart } from "../shared/types";

interface InlineToolPanelProps {
  part: ToolUIPart;
}

export const InlineToolPanel = memo(function InlineToolPanel({ part }: InlineToolPanelProps) {
  const [expanded, setExpanded] = useState(false);

  const isStreaming = part.state === "input-streaming";
  const hasOutput = part.state === "output-available" && part.output !== undefined;
  const hasError = part.state === "output-error";

  const inputStr = useMemo(
    () => (part.input !== undefined ? JSON.stringify(part.input, null, 2) : ""),
    [part.input],
  );
  const truncatedInput = inputStr.length > 200 ? inputStr.slice(0, 200) + "…" : inputStr;
  const inputNeedsToggle = inputStr.length > 200;

  const outputStr = hasOutput ? String(part.output) : "";
  const errorStr = hasError ? (part.errorText ?? "error") : "";

  const panelClass = `inline-tool${isStreaming ? " streaming" : ""}`;

  return (
    <div className={panelClass}>
      <div className="inline-tool-header">
        <span className="inline-tool-icon" aria-hidden="true">✦</span>
        <span className="inline-tool-name">
          {isStreaming ? (
            <>{part.toolName}<span className="streaming-dots" aria-hidden="true" /></>
          ) : (
            part.toolName
          )}
        </span>
      </div>
      {inputStr && (
        <>
          <div className="inline-tool-input">
            {expanded || !inputNeedsToggle ? inputStr : truncatedInput}
          </div>
          {inputNeedsToggle && (
            <button
              className="inline-tool-toggle"
              onClick={() => setExpanded((e) => !e)}
              type="button"
              aria-expanded={expanded}
              aria-label={expanded ? `Show less for ${part.toolName}` : `Show more for ${part.toolName}`}
            >
              {expanded ? "show less" : "show more"}
            </button>
          )}
        </>
      )}
      {hasOutput && (
        <div className="inline-tool-output">
          {outputStr}
          <div className="inline-tool-fade" />
        </div>
      )}
      {hasError && (
        <div className="inline-tool-output error">
          {errorStr}
        </div>
      )}
    </div>
  );
});
